import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { FiArrowRight, FiPlus } from "react-icons/fi";
import {
  EmptyState,
  PageHeader,
  StatusBadge,
} from "../../components/dashboard/DashboardUI";
import { fetchUserProcedures } from "../../redux/actions/ProcedureActions";
import { getCompanyValidSubscriptions } from "../../redux/actions/SubscriptionActions";
import { SERVICE_NAMES, requestTitle } from "../../utils/requestDisplay";
import { isStale, isUnpaid } from "../../utils/resumePayment";

const ONGOING = ["paid", "in_progress"];
const MAX_ONGOING = 5;

function UserHome() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authUser);
  const { activeCompany } = useSelector((state) => state.companies);
  const { procedures } = useSelector((state) => state.procedures);
  const { companyValidSubscriptions } = useSelector(
    (state) => state.subscriptions,
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    setLoading(true);
    Promise.all([
      dispatch(fetchUserProcedures(user.token)),
      activeCompany?._id
        ? dispatch(getCompanyValidSubscriptions(user.token, activeCompany._id))
        : null,
    ]).finally(() => setLoading(false));
  }, [user, activeCompany, dispatch]);

  const requests = (procedures || []).filter((r) => r.status !== "initiated");
  const unpaid = (procedures || []).filter((r) => isUnpaid(r) && !isStale(r));
  const ongoing = requests
    .filter((r) => ONGOING.includes(r.status))
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const finished = requests.filter((r) => r.status === "fulfilled");
  const subscriptions = activeCompany ? companyValidSubscriptions || [] : [];

  const stats = [
    { label: "Démarches en cours", value: ongoing.length },
    { label: "Démarches terminées", value: finished.length },
    { label: "Abonnements actifs", value: subscriptions.length },
  ];

  return (
    <>
      <PageHeader
        title={`Bonjour ${user?.firstname || ""}`}
        lead={
          activeCompany
            ? `Voici où en sont vos démarches et les services de ${activeCompany.businessName}.`
            : "Voici où en sont vos démarches."
        }
        actions={
          <button
            type="button"
            className="dash-button"
            onClick={() => navigate("/formalities")}
          >
            <FiPlus aria-hidden="true" /> Nouvelle démarche
          </button>
        }
      />

      {!loading && unpaid.length > 0 && (
        <div className="dash-notice">
          <span>
            {unpaid.length > 1
              ? `${unpaid.length} démarches attendent votre paiement.`
              : "Une démarche attend votre paiement."}
          </span>
          <Link
            to="/user/procedures?status=initiated"
            className="dash-card-link"
          >
            Voir et payer <FiArrowRight aria-hidden="true" />
          </Link>
        </div>
      )}

      <div className="dash-stats">
        {stats.map((stat) => (
          <div key={stat.label} className="dash-card dash-stat">
            <span>{stat.label}</span>
            <strong>{loading ? "…" : stat.value}</strong>
          </div>
        ))}
      </div>

      <div className="dash-home-grid">
        <section className="dash-card">
          <div className="dash-card-head">
            <h2 className="dash-card-title">Démarches en cours</h2>
            <Link to="/user/procedures" className="dash-card-link">
              Tout voir <FiArrowRight aria-hidden="true" />
            </Link>
          </div>

          {loading ? (
            <p className="dash-loading">Chargement…</p>
          ) : ongoing.length === 0 ? (
            <EmptyState title="Aucune démarche en cours">
              Vos démarches payées apparaîtront ici jusqu'à leur traitement.
            </EmptyState>
          ) : (
            <ul className="dash-list">
              {ongoing.slice(0, MAX_ONGOING).map((request) => (
                <li key={request.reference}>
                  <div>
                    <span className="dash-cell-title">
                      {requestTitle(request)}
                    </span>
                    <span className="dash-cell-sub">
                      #{request.reference} ·{" "}
                      {moment(request.createdAt).format("DD/MM/YYYY")}
                    </span>
                  </div>
                  <StatusBadge status={request.status} />
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="dash-card">
          <div className="dash-card-head">
            <h2 className="dash-card-title">Mes abonnements</h2>
            {activeCompany && (
              <Link to="/user/subscriptions" className="dash-card-link">
                Gérer <FiArrowRight aria-hidden="true" />
              </Link>
            )}
          </div>

          {!activeCompany ? (
            <EmptyState title="Aucune entreprise active">
              Créez ou choisissez une entreprise pour souscrire à nos services.
            </EmptyState>
          ) : loading ? (
            <p className="dash-loading">Chargement…</p>
          ) : subscriptions.length === 0 ? (
            <EmptyState title="Aucun abonnement actif">
              Domiciliation, administration, suivi juridique ou Gestion 360 :
              ajoutez les services utiles à votre entreprise.
            </EmptyState>
          ) : (
            <ul className="dash-list">
              {subscriptions.map((subscription) => (
                <li key={subscription._id || subscription.service}>
                  <span className="dash-cell-title">
                    {SERVICE_NAMES[subscription.service] ||
                      subscription.service}
                  </span>
                  <span className="dash-cell-sub">
                    {subscription.endsOn
                      ? `Jusqu'au ${moment(subscription.endsOn).format(
                          "DD/MM/YYYY",
                        )}`
                      : "Sans date de fin"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
}

export default UserHome;
